import { useEffect, useRef, useState, useCallback } from "react";

interface WebSocketMessage {
  type: string;
  payload: any;
  timestamp: Date;
}

interface UseWebSocketOptions {
  url: string;
  onMessage?: (message: WebSocketMessage) => void;
  onError?: (error: Event) => void;
  onOpen?: () => void;
  onClose?: () => void;
  reconnectAttempts?: number;
  reconnectInterval?: number;
  autoReconnect?: boolean;
  enabled?: boolean;
}

export const useWebSocket = (options: UseWebSocketOptions) => {
  const {
    url,
    onMessage,
    onError,
    onOpen,
    onClose,
    reconnectAttempts = 5,
    reconnectInterval = 3000,
    autoReconnect = true,
    enabled = true,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connect = useCallback(() => {
    if (!enabled) return;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectCountRef.current = 0;
        onOpen?.();
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = {
            ...JSON.parse(event.data),
            timestamp: new Date(),
          };
          setLastMessage(message);
          onMessage?.(message);
        } catch (err) {
          console.error("Failed to parse WebSocket message:", err);
        }
      };

      ws.onerror = (event) => {
        setError("WebSocket error occurred");
        onError?.(event);
      };

      ws.onclose = () => {
        setIsConnected(false);
        onClose?.();

        // Auto-reconnect logic
        if (autoReconnect && reconnectCountRef.current < reconnectAttempts) {
          reconnectCountRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(
              `Attempting to reconnect... (${reconnectCountRef.current}/${reconnectAttempts})`,
            );
            connect();
          }, reconnectInterval);
        }
      };
    } catch (err) {
      setError("Failed to create WebSocket connection");
    }
  }, [
    url,
    onMessage,
    onError,
    onOpen,
    onClose,
    reconnectAttempts,
    reconnectInterval,
    autoReconnect,
    enabled,
  ]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback(
    (message: any) => {
      if (wsRef.current && isConnected) {
        wsRef.current.send(JSON.stringify(message));
      } else {
        console.warn("WebSocket is not connected");
      }
    },
    [isConnected],
  );

  useEffect(() => {
    if (enabled) {
      connect();
    }
    return () => disconnect();
  }, [connect, disconnect, enabled]);

  return {
    isConnected,
    lastMessage,
    error,
    sendMessage,
    disconnect,
    connect,
  };
};
